/** marked + DOMPurify via CDN (pas de dépendance npm — aligné pdf.js mobile). */
const MARKED_CDN = "https://cdnjs.cloudflare.com/ajax/libs/marked/15.0.7/marked.min.js";
const PURIFY_CDN = "https://cdnjs.cloudflare.com/ajax/libs/dompurify/3.2.4/purify.min.js";

const MARKDOWN_EXTENSIONS = new Set(["md", "markdown", "mdown", "mkd"]);

/** Même idée que le Kanban : JSON BlockNote dans le `.md`, hors aperçu. */
const BLOCKS_MARKER = "<!--BLOCKS_JSON";
const BLOCKS_MARKER_KANBAN = "<!--BLOCKS_JSON-->";

function parseBlocksJson(raw) {
  const text = String(raw || "").trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Sépare le Markdown lisible du document BlockNote (couleurs, fonds…).
 * @param {string} source
 * @returns {{ markdown: string, blocks: object[] | null }}
 */
export function coffreSplitMarkdownBlocks(source) {
  const raw = String(source ?? "").replace(/\r\n/g, "\n");
  const wrapped = raw.match(/<!--BLOCKS_JSON\s*\n([\s\S]*?)\n-->\s*$/);
  if (wrapped) {
    const markdown = raw.slice(0, wrapped.index).replace(/\s+$/, "");
    return { markdown, blocks: parseBlocksJson(wrapped[1]) };
  }
  const idx = raw.lastIndexOf(BLOCKS_MARKER_KANBAN);
  if (idx >= 0) {
    const markdown = raw.slice(0, idx).replace(/\s+$/, "");
    return { markdown, blocks: parseBlocksJson(raw.slice(idx + BLOCKS_MARKER_KANBAN.length)) };
  }
  return { markdown: raw, blocks: null };
}

/** Markdown seul — aperçu, PDF, impression. */
export function coffreStripMarkdownBlocks(source) {
  return coffreSplitMarkdownBlocks(source).markdown;
}

/**
 * @param {string} markdown
 * @param {object[] | null} blocks
 */
export function coffreComposeMarkdownBlocks(markdown, blocks) {
  const md = String(markdown ?? "").replace(/\s+$/, "");
  if (!Array.isArray(blocks) || blocks.length === 0) return md;
  try {
    const json = JSON.stringify(blocks);
    return md ? `${md}\n\n${BLOCKS_MARKER}\n${json}\n-->` : `${BLOCKS_MARKER}\n${json}\n-->`;
  } catch {
    return md;
  }
}

/** Document BlockNote sérialisable (sans nœuds DOM / refs circulaires). */
export function coffreSerializeBlockNoteDocument(document) {
  const visited = new WeakSet();
  const clean = (value, depth = 0) => {
    if (depth > 40) return null;
    if (value == null || typeof value !== "object") return value;
    if (typeof Node !== "undefined" && value instanceof Node) return null;
    if (visited.has(value)) return null;
    visited.add(value);
    if (Array.isArray(value)) return value.map((item) => clean(item, depth + 1));
    const out = {};
    for (const key of Object.keys(value)) {
      if (key.startsWith("__") || key === "stateNode" || key === "ownerDocument" || key === "parentNode") {
        continue;
      }
      try {
        const next = clean(value[key], depth + 1);
        if (next !== undefined) out[key] = next;
      } catch {
        /* propriété non sérialisable */
      }
    }
    return out;
  };
  const cleaned = clean(document);
  return Array.isArray(cleaned) && cleaned.length > 0 ? cleaned : null;
}

let markdownLibsPromise = null;

function loadScript(src, datasetKey) {
  return new Promise((resolve, reject) => {
    if (datasetKey === "marked" && window.marked) {
      resolve();
      return;
    }
    if (datasetKey === "purify" && window.DOMPurify) {
      resolve();
      return;
    }
    const sel = `script[data-coffre-md="${datasetKey}"]`;
    const existing = document.querySelector(sel);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`${datasetKey} load failed`)), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.coffreMd = datasetKey;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`${datasetKey} load failed`));
    document.head.appendChild(script);
  });
}

function loadMarkdownLibs() {
  if (!markdownLibsPromise) {
    markdownLibsPromise = loadScript(MARKED_CDN, "marked").then(() => loadScript(PURIFY_CDN, "purify"));
  }
  return markdownLibsPromise;
}

/** @param {string} filename */
export function coffreIsMarkdownFilename(filename) {
  const dot = String(filename || "").lastIndexOf(".");
  if (dot < 0) return false;
  return MARKDOWN_EXTENSIONS.has(String(filename).slice(dot + 1).toLowerCase());
}

/**
 * Markdown → HTML sanitisé pour aperçu Coffre.
 * @param {string} source
 * @returns {Promise<string>}
 */
export async function coffreRenderMarkdownHtml(source) {
  await loadMarkdownLibs();
  const marked = window.marked;
  const purify = window.DOMPurify;
  if (!marked?.parse || !purify?.sanitize) {
    throw new Error("Bibliothèques Markdown indisponibles.");
  }
  const raw = marked.parse(coffreStripMarkdownBlocks(source), { gfm: true, breaks: true });
  return purify.sanitize(raw, { USE_PROFILES: { html: true } });
}
